import type { Aftercare, SalesLineType } from '@/types';
import { displayRemark, lineLabels, isDeferredMissed } from '@/lib/salesAftercare.mjs';
/**
 * 客户专属只读分享链接
 * - 将单据数据压缩编码进 URL（lz-string compressToEncodedURIComponent）
 * - 通过 #/share?d=<code> 独立路由渲染只读视图（不进入主应用）
 * - 凭客户手机号后4位验证查看，防止转发给无关人员
 */

import LZString from 'lz-string';
import { generateQuotationShareHtml } from './shareDocument';
import type { ShareTemplate } from './shareTemplates';
import type { PaymentWatermark } from './salesShare';

const ENC_PREFIX = 'enc:v2:';
const ENC_PREFIX_V3 = 'enc:v3:';

// #752 安全加固：v3 起 PBKDF2 迭代从 10 万提升到 60 万（与本地加密 crypto.ts 对齐），
// 大幅提高 PIN 暴力破解成本；v2 旧链接（30 天 TTL 内）按旧参数继续解密，不受影响。
const PBKDF2_ITERATIONS_V2 = 100_000;
const PBKDF2_ITERATIONS = 600_000;

function bufToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
}

function base64ToBuf(b64: string): Uint8Array {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

async function deriveShareKey(pin: string, salt: Uint8Array, iterations: number): Promise<CryptoKey> {
  const enc = new TextEncoder();
  const material = await crypto.subtle.importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveBits', 'deriveKey']);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt.buffer as ArrayBuffer, iterations, hash: 'SHA-256' },
    material,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt']
  );
}

async function encryptPayload(plain: string, pin: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveShareKey(pin, salt, PBKDF2_ITERATIONS);
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plain));
  return `${ENC_PREFIX_V3}${bufToBase64(salt)}:${bufToBase64(iv)}:${bufToBase64(ct)}`;
}

async function decryptPayload(code: string, pin: string, prefix: string, iterations: number): Promise<string | null> {
  try {
    const body = code.slice(prefix.length);
    const [saltB64, ivB64, ctB64] = body.split(':');
    if (!saltB64 || !ivB64 || !ctB64) return null;
    const salt = base64ToBuf(saltB64);
    const iv = base64ToBuf(ivB64);
    const ct = base64ToBuf(ctB64);
    const key = await deriveShareKey(pin, salt, iterations);
    const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: iv.buffer as ArrayBuffer }, key, ct.buffer as ArrayBuffer);
    return new TextDecoder().decode(plain);
  } catch {
    return null;
  }
}

function isSecureContext(): boolean {
  return typeof window !== 'undefined' && window.isSecureContext;
}

export interface ShareDocItem {
  productName: string;
  qty: number;
  subUnitQty: number;
  perUnitQty?: number;
  subUnit: string;
  wholesalePrice: number;
  amount: number;
  paid?: boolean;
  printPrice?: boolean;
  showAmount?: boolean;
  showPrice?: boolean;
  remark?: string;
  lineType?: SalesLineType;
    aftercare?: Aftercare;
  priceLabel?: '优惠价' | '优惠退货';
}

export interface ShareDocPayload {
  docType: 'quotation' | 'delivery' | 'sales' | 'inbound' | 'po';
  docTitle: string;
  quotationId: string;
  customerName: string;
  customerPhone?: string;
  customerAddress?: string;
  date: string;
  items: ShareDocItem[];
  totalAmount: number;
  paidAmount: number;
  printHidePaid: boolean;
  fontSize: number;
  /** 整单备注（如"结单时付一半"） */
  remark?: string;
  /** 销售单分享时的结算状态快照；旧分享链接不包含此字段。 */
  paymentWatermark?: PaymentWatermark;
  /** 手机号后4位（或自定义口令）的校验哈希 */
  pinHash: string;
  /** 链接过期时间戳（毫秒），0 表示永久 */
  expireAt: number;
}

/** 简单确定性哈希（djb2 变体 + 盐），用于口令校验（前端 best-effort 防转发） */
export function hashPin(pin: string): string {
  const salted = `changfa::${pin}::share`;
  let h1 = 5381;
  let h2 = 52711;
  for (let i = 0; i < salted.length; i++) {
    const c = salted.charCodeAt(i);
    h1 = ((h1 << 5) + h1 + c) >>> 0;
    h2 = ((h2 << 5) + h2 + c) >>> 0;
  }
  return `${h1.toString(36)}${h2.toString(36)}`;
}

/** 取手机号后4位作为口令；无手机号时返回空 */
export function pinFromPhone(phone?: string): string {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  return digits.slice(-4);
}

function compactPayload(payload: ShareDocPayload): unknown[] {
  return [
    payload.docType,
    payload.docTitle,
    payload.quotationId,
    payload.customerName,
    payload.customerPhone || '',
    payload.customerAddress || '',
    payload.date,
    payload.items.map((it) => [
      it.productName || '',
      it.qty || 0,
      it.subUnitQty || 0,
      it.subUnit || '',
      it.wholesalePrice || 0,
      it.amount || 0,
      it.paid ? 1 : 0,
      it.printPrice ? 1 : 0,
      displayRemark(it),
      it.lineType === 'return' ? 1 : it.lineType === 'exchange' ? 2 : it.lineType === 'outOfStock' ? 3 : it.lineType === 'missed' ? 4 : it.lineType === 'resend' ? 5 : it.lineType === 'cancelled' ? 6 : 0,
      it.perUnitQty ?? null,
      it.lineType === 'cancelled' || isDeferredMissed(it) || it.showAmount === false ? 0 : 1,
      it.showPrice === false ? 0 : 1,
      it.priceLabel || '',
    ]),
    Math.round(payload.totalAmount * 100),
    Math.round(payload.paidAmount * 100),
    payload.printHidePaid ? 1 : 0,
    payload.fontSize || 13,
    payload.pinHash,
    payload.expireAt || 0,
    payload.remark || '',
    payload.paymentWatermark || '',
  ];
}

function parseCompact(c: unknown[]): ShareDocPayload | null {
  if (!Array.isArray(c) || c.length < 14) return null;
  const items = Array.isArray(c[7]) ? c[7] : [];
  return {
    docType: String(c[0]) as ShareDocPayload['docType'],
    docTitle: String(c[1] || '送货单'),
    quotationId: String(c[2]),
    customerName: String(c[3]),
    customerPhone: c[4] ? String(c[4]) : undefined,
    customerAddress: c[5] ? String(c[5]) : undefined,
    date: String(c[6]),
    items: items.map((r: unknown) => {
      const row = Array.isArray(r) ? r : [];
      const lineTypeCode = Number(row[9] ?? 0);
      return {
        productName: String(row[0] || ''),
        qty: Number(row[1] ?? 0),
        subUnitQty: Number(row[2] ?? 0),
        perUnitQty: row[10] == null ? undefined : Number(row[10]),
        subUnit: String(row[3] || ''),
        wholesalePrice: Number(row[4] ?? 0),
        amount: Number(row[5] ?? 0),
        showAmount: row[11] !== 0,
        showPrice: row[12] !== 0,
        priceLabel: row[13] === '优惠价' || row[13] === '优惠退货' ? row[13] : undefined,
        paid: !!row[6],
        printPrice: !!row[7],
        remark: String(row[8] || ''),
        lineType: lineTypeCode === 1 ? 'return' : lineTypeCode === 2 ? 'exchange' : lineTypeCode === 3 ? 'outOfStock' : lineTypeCode === 4 ? 'missed' : lineTypeCode === 5 ? 'resend' : lineTypeCode === 6 ? 'cancelled' : 'normal',
      };
    }),
    totalAmount: Number(c[8] ?? 0) / 100,
    paidAmount: Number(c[9] ?? 0) / 100,
    printHidePaid: !!c[10],
    fontSize: Number(c[11] || 13),
    pinHash: String(c[12] || ''),
    expireAt: Number(c[13] || 0),
    remark: c[14] ? String(c[14]) : undefined,
    paymentWatermark: ['已付款', '部分付款', '未付款'].includes(String(c[15]))
      ? String(c[15]) as PaymentWatermark
      : undefined,
  };
}

/** 将分享数据编码为加密字符串（需 PIN 解密） */
export async function encodeShareData(payload: ShareDocPayload, pin: string): Promise<string> {
  if (!isSecureContext()) {
    throw new Error('分享链接加密需要 HTTPS 或 localhost 环境');
  }
  const plain = JSON.stringify(compactPayload(payload));
  return encryptPayload(plain, pin);
}

/** 从加密字符串解码分享数据；格式非法或 PIN 错误返回 null */
export async function decodeShareData(code: string, pin?: string): Promise<ShareDocPayload | null> {
  try {
    if (code.startsWith(ENC_PREFIX_V3)) {
      if (!pin) return null;
      const plain = await decryptPayload(code, pin, ENC_PREFIX_V3, PBKDF2_ITERATIONS);
      if (!plain) return null;
      const c = JSON.parse(plain);
      return parseCompact(c);
    }
    if (code.startsWith(ENC_PREFIX)) {
      // v2 旧链接：按 10 万次迭代解密，保证已发出的 30 天内链接继续可用
      if (!pin) return null;
      const plain = await decryptPayload(code, pin, ENC_PREFIX, PBKDF2_ITERATIONS_V2);
      if (!plain) return null;
      const c = JSON.parse(plain);
      return parseCompact(c);
    }

    // 旧版可逆压缩链接：保留只读解析能力，但不再允许生成
    const json = LZString.decompressFromEncodedURIComponent(code);
    if (!json) return null;
    const c = JSON.parse(json);
    return parseCompact(c);
  } catch {
    return null;
  }
}

/**
 * 生成完整分享链接
 * @param payload 单据数据（pinHash 已设置）
 * @param pin 查看口令；默认取客户手机号后 4 位
 */
export async function buildShareLink(payload: ShareDocPayload, pin?: string): Promise<string> {
  const actualPin = pin || pinFromPhone(payload.customerPhone);
  if (!actualPin) {
    throw new Error('生成分享链接需要口令（手机号后 4 位或自定义 PIN）');
  }
  const code = await encodeShareData(payload, actualPin);
  const { origin, pathname } = window.location;
  return `${origin}${pathname}#/share?d=${code}`;
}

/** 判断是否为 v2 加密分享码（必须凭口令解密，无法预先解析内容） */
export function isEncryptedShareCode(code: string): boolean {
  return code.startsWith(ENC_PREFIX);
}

/** 从当前地址解析分享码 */
export function parseShareCodeFromLocation(): string | null {
  const hash = window.location.hash || '';
  const m = hash.match(/^#\/share\?d=(.+)$/);
  return m ? m[1] : null;
}

/** 校验口令（用于解密后验证） */
export function verifyPin(payload: ShareDocPayload, input: string): boolean {
  if (!payload.pinHash) return true; // 未设口令则直接放行
  return hashPin(input.trim()) === payload.pinHash;
}

/** 检查链接是否过期 */
export function isExpired(payload: ShareDocPayload): boolean {
  return payload.expireAt > 0 && Date.now() > payload.expireAt;
}

/** 用只读视图渲染单据 HTML（复用 generateQuotationShareHtml）
 * #798：可选传入分享模板（模板存本机 localStorage，不随 URL 传递——
 * 历史链接与对方打开时一律回退内置布局，保证向后兼容） */
export function renderShareDocHtml(payload: ShareDocPayload, template?: ShareTemplate): string {
  return generateQuotationShareHtml({
    quotationId: payload.quotationId,
    customerName: payload.customerName,
    customerPhone: payload.customerPhone,
    customerAddress: payload.customerAddress,
    date: payload.date,
    items: payload.items,
    totalAmount: payload.totalAmount,
    paidAmount: payload.paidAmount,
    printHidePaid: payload.printHidePaid,
    fontSize: payload.fontSize,
    remark: payload.remark,
    docTitle: payload.docTitle,
    docType: payload.docType,
    paymentWatermark: payload.paymentWatermark,
    template,
  });
}

/** 默认链接有效期：30 天 */
export const SHARE_LINK_DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000;
